import React, { FC, MouseEvent, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  StarIcon,
  Text
} from 'stubben-edge-unified-frontend-react-components';
import useFavourites from '@/hooks/useFavourites';
import useStore from '@/hooks/useStore';
import FavouriteToggle from '@/components/common/FavouriteToggle';
import { FavouriteItem } from '@/interfaces/Favourite.interface';
import { MenuGroupInterface } from '../../types';
import { navLinkToTestSegment } from '../../navTestId';
import { resolveFavouriteIcon } from '../../resolveFavouriteIcon';
import { useSidebarCollapsed } from '../../../Sidebar/SidebarCollapsedContext';
import SidebarTooltip from '../../../Sidebar/SidebarTooltip';
import SidebarFlyout from '../../../Sidebar/SidebarFlyout';

interface FavouritesSectionProps {
  navGroups: MenuGroupInterface[];
}

const collapsedButtonClass = (isActive: boolean) =>
  `flex h-12 w-full cursor-pointer items-center justify-center hover:border-r-2 hover:border-r-primary hover:bg-gray-50 focus:border-r-primary ${
    isActive ? 'border-r-2 border-r-primary bg-gray-50' : ''
  }`;

const isFavouriteActive = (favourite: FavouriteItem) =>
  window.location.pathname + window.location.search === favourite.path;

interface FavouriteFlyoutRowProps {
  favourite: FavouriteItem;
  onNavigate: (path: string) => void;
  onRemove: (id: string) => void;
}

const FavouriteFlyoutRow: FC<FavouriteFlyoutRowProps> = ({
  favourite,
  onNavigate,
  onRemove
}) => {
  const isActive = isFavouriteActive(favourite);
  const segment = navLinkToTestSegment(favourite.path);

  return (
    <div
      className={`group mx-2.5 flex items-center gap-1 rounded hover:bg-gray-50 ${
        isActive ? 'bg-primary-50 font-semibold text-primary' : 'text-gray-500'
      }`}
    >
      <button
        type="button"
        data-testid={`sidebar-favourite-${segment}`}
        onClick={() => onNavigate(favourite.path)}
        className="min-w-0 flex-1 cursor-pointer px-3 py-2 text-left"
      >
        <Text
          size="sm"
          variant={isActive ? 'primary' : 'quaternary'}
          mode={isActive ? 'brand' : 'normal'}
          bold={isActive ? 'medium' : 'regular'}
          testIdentifier={`sidebar-favourite-label-${segment}`}
        >
          {favourite.title}
        </Text>
      </button>
      <div
        className="pr-1"
        onClick={(event: MouseEvent) => event.stopPropagation()}
      >
        <FavouriteToggle
          isFavourited
          hoverReveal
          size={16}
          testIdentifier={`sidebar-favourite-unfavourite-${segment}`}
          onToggle={() => onRemove(favourite.id)}
        />
      </div>
    </div>
  );
};

const FavouritesSection: FC<FavouritesSectionProps> = ({ navGroups }) => {
  const { collapsed } = useSidebarCollapsed();
  const navigate = useNavigate();
  const { interfaceStore } = useStore();
  const { favourites, removeFavourite } = useFavourites();
  const [flyoutOpen, setFlyoutOpen] = useState(false);

  if (favourites.length === 0) return null;

  const navigateToFavourite = (path: string) => {
    navigate(path);
    interfaceStore.toggleMenuDrawer(false);
  };

  if (collapsed) {
    const hasActiveFavourite = favourites.some(isFavouriteActive);
    const iconColor =
      hasActiveFavourite || flyoutOpen ? '#106df6' : '#667085';

    return (
      <SidebarTooltip
        label="Favourites"
        disabled={flyoutOpen}
        testIdentifier="sidebar-favourites-tooltip"
      >
        <SidebarFlyout
          buttonContent={<StarIcon size={18} color={iconColor} />}
          buttonClassName={collapsedButtonClass(
            hasActiveFavourite || flyoutOpen
          )}
          buttonTestId="sidebar-favourites"
          buttonAriaLabel="Favourites"
          panelTestId="sidebar-favourites-flyout"
          onOpenChange={setFlyoutOpen}
        >
          <div
            className="flex max-h-64 flex-col gap-1 overflow-y-auto"
            data-testid="sidebar-favourites-panel"
          >
            {favourites.map((favourite) => (
              <FavouriteFlyoutRow
                key={favourite.id}
                favourite={favourite}
                onNavigate={navigateToFavourite}
                onRemove={removeFavourite}
              />
            ))}
          </div>
        </SidebarFlyout>
      </SidebarTooltip>
    );
  }

  return (
    <>
      <div
        className="px-6 py-4"
        data-testid="sidebar-nav-section-favourites"
      >
        <Text
          size="sm"
          bold="heavy"
          variant="secondary"
          testIdentifier="sidebar-nav-section-label-favourites"
        >
          Favourites
        </Text>
      </div>
      {favourites.map((favourite) => {
        const isActive = isFavouriteActive(favourite);
        const icon = resolveFavouriteIcon(favourite, navGroups);
        const segment = navLinkToTestSegment(favourite.path);
        const renderedIcon = React.isValidElement(icon)
          ? React.cloneElement(icon as React.ReactElement, {
              size: 18,
              color: isActive ? '#106df6' : '#667085'
            })
          : icon;

        return (
          <div
            key={favourite.id}
            className={`group flex w-full items-center hover:border-r-2 hover:border-r-primary hover:bg-gray-50 focus-within:border-r-primary ${
              isActive ? 'border-r-2 border-r-primary bg-gray-50' : ''
            }`}
          >
            <button
              type="button"
              data-testid={`sidebar-favourite-${segment}`}
              onClick={() => navigateToFavourite(favourite.path)}
              className="flex min-w-0 flex-1 cursor-pointer items-center gap-3 p-6 text-left"
            >
              {renderedIcon}
              <Text
                size="sm"
                variant={isActive ? 'primary' : 'quaternary'}
                mode={isActive ? 'brand' : 'normal'}
                bold={isActive ? 'medium' : 'regular'}
                testIdentifier={`sidebar-favourite-label-${segment}`}
              >
                {favourite.title}
              </Text>
            </button>
            <div className="pr-4">
              <FavouriteToggle
                isFavourited
                hoverReveal
                testIdentifier={`sidebar-favourite-unfavourite-${segment}`}
                onToggle={() => removeFavourite(favourite.id)}
              />
            </div>
          </div>
        );
      })}
      <div
        className="px-6 py-4"
        data-testid="sidebar-nav-section-divider-favourites"
      >
        <div className="border-b border-gray-200" />
      </div>
    </>
  );
};

export default FavouritesSection;
